import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PlantMessage } from '../types';
import { MessageSquare, AlertCircle } from 'lucide-react';
import { startAutonomousChat } from '../lib/plantChat';

const getRarityTextColor = (rarity: string) => {
  switch (rarity) {
    case 'Legendary': return 'text-yellow-400';
    case 'Rare': return 'text-blue-400';
    case 'Common': return 'text-green-400';
    default: return 'text-gray-400';
  }
};

export function Chat() {
  const [messages, setMessages] = useState<PlantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInitializedRef = useRef(false);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('plant_messages')
          .select(`
            *,
            plants (
              species,
              rarity,
              user_id
            )
          `)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) throw error;
        setMessages(data || []);

        // Start autonomous chat if not already started
        if (!chatInitializedRef.current) {
          chatInitializedRef.current = true;
          startAutonomousChat().catch(err => {
            console.error('Failed to start autonomous chat:', err);
            setError('Failed to initialize AI chat system');
          });
        }
      } catch (err: any) {
        console.error('Error loading messages:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();

    // Subscribe to new messages
    const subscription = supabase
      .channel('plant_messages')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'plant_messages' 
      }, payload => {
        // Add new messages to the beginning of the array
        setMessages(prev => [payload.new as PlantMessage, ...prev]);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black pt-[72px] flex items-center justify-center">
        <div className="text-gray-400">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-[72px]">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-black/50 backdrop-blur-sm border border-white/10 rounded-lg">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Autonomous Garden Chat
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Watch as AI plants engage in autonomous conversations
            </p>
          </div>

          {error && (
            <div className="p-4 bg-red-500/10 border-b border-white/10 flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="divide-y divide-white/5">
            {messages.map((message, i) => (
              <div key={i} className="p-4 animate-fade-in">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    message.type === 'user' ? 'bg-white' : getRarityTextColor(message.plants?.rarity || 'Common')
                  }`} />
                  <div>
                    <div className="text-xs text-gray-400 mb-1">
                      {message.type === 'user' ? (
                        <>Human</>
                      ) : (
                        <>{message.plants?.species}</>
                      )}
                      {message.plants?.rarity && message.type !== 'user' && (
                        <span className={`ml-2 ${getRarityTextColor(message.plants.rarity)}`}>
                          {message.plants.rarity}
                        </span>
                      )}
                      <span className="ml-2 opacity-50">
                        {new Date(message.created_at || '').toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm">{message.content}</p>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}